import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  CustomerOrderLineItem,
  InstallationAddress,
  OrderData,
  OrderProduct,
  OrderRequiredField,
} from '../../../core/models/order.model';
import { CurrencyBRLPipe } from '../../pipes/currency-brl.pipe';
import { IconComponent } from '../icon/icon.component';
import { Icon } from '../../../core/models/icon.model';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import {
  getOrderDisplayTotal,
  getOrderDisplayUnitPrice,
  getOrderPriceSuffixKind,
  getOrderTotalSuffixKind,
  isFreeOrderPrice,
  MindOrderListPriceUi,
  OrderRecurrencyPriceItem,
  resolveMindOrderListPrice,
  shouldApplyMindOrderPriceRules,
} from '../../../core/utils/order-recurrency-price.util';

/** `name` values on line item requiredFields that represent the domain purchased/configured. */
const DOMAIN_REQUIRED_FIELD_NAMES = new Set([
  'com_br_domain',
  'com_domain',
  'net_domain',
  'microsoft_domain',
  'google_ws_domain',
  'email_domain',
]);

const DOMAIN_FIELD_ORDER = [
  'com_br_domain',
  'com_domain',
  'net_domain',
  'microsoft_domain',
  'google_ws_domain',
  'email_domain',
] as const;

const DOMAIN_PREFERENCE_FIELD_NAMES = ['domain_preference', 'tenant_preference'] as const;

type DomainPreferenceKind = 'new' | 'existing' | 'transfer';

type OrderContentDisplayProduct = OrderProduct & {
  displayTrackId: string;
  parentProductUuid?: string | null;
};

interface OrderContentProductGroup {
  groupTrackId: string;
  products: OrderContentDisplayProduct[];
}

@Component({
  selector: 'umkt-order-content',
  standalone: true,
  imports: [CurrencyBRLPipe, DatePipe, IconComponent, I18nPipe],
  templateUrl: './order-content.component.html',
  styleUrl: './order-content.component.scss',
})
export class OrderContentComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  orderDetails: OrderData = {} as OrderData;
  displayProducts: OrderContentDisplayProduct[] = [];
  displayProductGroups: OrderContentProductGroup[] = [];
  status: string = '';
  icon: Icon = {} as Icon;
  statusClass: string = '';
  iconBrandPath: string = '';
  cardFlag: string = '';

  private i18nService = inject(I18nService);
  private router = inject(Router);
  private langSubscription?: Subscription;

  ngOnInit(): void {
    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      this.mapStatus();
    });

    this.mapStatus();
    this.mapToBrandCard();
    this.buildDisplayProducts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderDetails']) {
      this.mapStatus();
      this.mapToBrandCard();
      this.buildDisplayProducts();
    }
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  private buildDisplayProducts(): void {
    const od = this.orderDetails;
    if (!od) {
      this.displayProducts = [];
      this.displayProductGroups = [];
      return;
    }

    if (Array.isArray(od.lineItems) && od.lineItems.length > 0) {
      this.displayProducts = od.lineItems.map((li, index) => this.fromLineItem(li, index));
    } else {
      const raw = Array.isArray(od.products) ? od.products : [];
      this.displayProducts = raw.map((p, index) => this.fromOrderProduct(p, index));
    }

    this.displayProductGroups = this.groupProductsByDomain(this.displayProducts);
  }

  /**
   * Junta linhas que compartilham o mesmo domínio (valor normalizado em qualquer campo de domínio).
   * Itens sem domínio continuam um card cada.
   */
  private groupProductsByDomain(
    products: OrderContentDisplayProduct[]
  ): OrderContentProductGroup[] {
    if (products.length === 0) {
      return [];
    }

    const domainKeyToGroupIndex = new Map<string, number>();
    const groups: OrderContentDisplayProduct[][] = [];

    for (const p of products) {
      const domainKey = this.getDomainGroupingKey(p);
      if (domainKey === null) {
        groups.push([p]);
        continue;
      }
      const existing = domainKeyToGroupIndex.get(domainKey);
      if (existing === undefined) {
        domainKeyToGroupIndex.set(domainKey, groups.length);
        groups.push([p]);
      } else {
        groups[existing].push(p);
      }
    }

    return groups.map((groupProducts) => {
      const domainKey = this.getDomainGroupingKey(groupProducts[0]);
      const groupTrackId =
        domainKey !== null
          ? `domain-group:${encodeURIComponent(domainKey)}`
          : groupProducts[0].displayTrackId;

      return { groupTrackId, products: groupProducts };
    });
  }

  getDomainGroupingKey(product: OrderProduct): string | null {
    const fields = product.requiredFields ?? [];
    for (const name of DOMAIN_FIELD_ORDER) {
      const f = fields.find((x) => x.name === name);
      const v = String(f?.value ?? '').trim().toLowerCase();
      if (v) {
        return v;
      }
    }
    return null;
  }

  getGroupDomainPanelProduct(group: OrderContentProductGroup): OrderContentDisplayProduct | null {
    for (const p of group.products) {
      if (this.getVisibleRequiredFields(p).length > 0) {
        return p;
      }
    }
    return null;
  }

  private fromLineItem(li: CustomerOrderLineItem, index: number): OrderContentDisplayProduct {
    const name =
      li.parentProductName && li.productName
        ? `${li.parentProductName} - ${li.productName}`
        : li.productName;
    const matchingProduct = this.orderDetails.products?.find(
      (p) => p.uuid === li.productUuid || (!!li.productSku && p.sku === li.productSku)
    );
    return {
      uuid: li.productUuid,
      name,
      quantity: li.quantity,
      price: li.price,
      recurrencePrice: li.recurrencePrice ?? matchingProduct?.recurrencePrice ?? null,
      plan: li.plan ?? '',
      sku: li.productSku,
      variable: true,
      requiredFields: this.normalizeRequiredFields(li.requiredFields),
      paymentRecurrency:
        li.paymentRecurrency ?? matchingProduct?.paymentRecurrency ?? null,
      parentProductUuid: li.parentProductUuid,
      displayTrackId: `li-${index}-${li.productUuid}-${li.price}-${li.confirmationDate ?? ''}`,
    };
  }

  private normalizeRequiredFields(
    fields: CustomerOrderLineItem['requiredFields']
  ): OrderRequiredField[] {
    if (!Array.isArray(fields) || fields.length === 0) {
      return [];
    }
    return fields.map((f) => ({
      uuid: f.uuid,
      name: f.name,
      label: f.label,
      type: f.type,
      description: f.description,
      value: f.value != null ? String(f.value) : '',
    }));
  }

  private fromOrderProduct(p: OrderProduct, index: number): OrderContentDisplayProduct {
    return {
      uuid: p.uuid,
      name: p.name,
      quantity: p.quantity,
      price: p.price,
      recurrencePrice: p.recurrencePrice ?? null,
      plan: p.plan ?? '',
      sku: p.sku ?? '',
      variable: p.variable ?? true,
      requiredFields: p.requiredFields ?? [],
      paymentRecurrency: p.paymentRecurrency ?? null,
      displayTrackId: `p-${index}-${p.uuid}-${p.price}`,
    };
  }

  private mapToBrandCard() {
    switch (this.orderDetails.card?.cardFlag.toLowerCase()) {
      case 'visa':
        this.iconBrandPath = 'assets/images/credit-cards/visa.svg';
        this.cardFlag = 'visa';
        break;
      case 'mastercard':
        this.iconBrandPath = 'assets/images/credit-cards/mastercard.svg';
        this.cardFlag = 'mastercard';
        break;
      case 'amex':
        this.iconBrandPath = 'assets/images/credit-cards/amex.svg';
        this.cardFlag = 'amex';
        break;
      case 'unknown':
        this.iconBrandPath = 'assets/images/credit-cards/credit-card.svg';
        this.cardFlag = 'unknown';
        break;
    }
  }

  private mapStatus() {
    switch ((this.orderDetails.status ?? '').toLowerCase()) {
      case 'completed':
      case 'partial_completed':
      case 'created':
      case 'success':
      case 'done':
      case 'sucesso':
      case 'approved':
      case 'paid':
      case 'delivered':
        this.status = this.i18nService.instant('components.orderContent.status.completed');
        this.icon = {
          name: 'check-circulo',
          type: 'mondrian-icons',
        };
        break;
      case 'in_progress':
      case 'analysis':
      case 'pending_activation':
      case 'on_hold':
      case 'pending':
      case 'processing':
      case 'analyzing':
      case 'awaiting-cpc':
      case 'awaiting-mind':
        this.status = this.i18nService.instant('components.orderContent.status.analysis');
        this.icon = {
          name: 'circulo-exclamacao',
          type: 'mondrian-icons',
        };
        break;
      case 'failed_cle':
      case 'failed_credit':
      case 'cancelled':
      case 'refunded':
      case 'failed':
      case 'not_found':
      case 'bad_request':
      case 'rejected':
      case 'canceled':
        this.status = this.i18nService.instant('components.orderContent.status.cancelled');
        this.icon = {
          name: 'circulo-exclamacao',
          type: 'mondrian-icons',
        };
        break;
      default:
        break;
    }
  }

  /** Promo/cents rules when paymentMethod is MIND or API sent `extras`. */
  private shouldUseMindPriceUi(): boolean {
    return shouldApplyMindOrderPriceRules(
      this.orderDetails?.paymentMethod,
      this.orderDetails?.extras
    );
  }

  private toRecurrencyItem(
    product: OrderProduct & { parentProductUuid?: string | null }
  ): OrderRecurrencyPriceItem {
    return {
      price: product.price,
      quantity: product.quantity,
      variationUuid: product.uuid,
      productUuid: product.parentProductUuid,
      paymentRecurrency: product.paymentRecurrency,
    };
  }

  getMonthlyPrice(product: OrderProduct): number {
    return getOrderDisplayUnitPrice(
      this.toRecurrencyItem(product),
      this.orderDetails?.paymentMethod,
      this.orderDetails?.extras
    );
  }

  getMindListPrice(product: OrderProduct): MindOrderListPriceUi | null {
    if (!this.shouldUseMindPriceUi()) {
      return null;
    }
    return resolveMindOrderListPrice(product.price, this.orderDetails?.extras);
  }

  formatMindOriginalPrice(price: number): string {
    return price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  getMindPromoDurationKey(promo: MindOrderListPriceUi): string {
    return promo.promotionMonths === 1
      ? 'components.productCard.promoFirstMonth'
      : 'components.productCard.promoFirstMonths';
  }

  getProductVariationUuid(product: OrderProduct): string | undefined {
    return product.uuid || undefined;
  }

  isFreeProductPrice(product: OrderProduct): boolean {
    return isFreeOrderPrice(product.price);
  }

  getTotalMonthlyPrice(): number {
    return getOrderDisplayTotal(
      this.displayProducts.map((p) => this.toRecurrencyItem(p)),
      this.orderDetails.total || 0,
      this.orderDetails?.paymentMethod,
      this.orderDetails?.extras
    );
  }

  getMonthlySuffix(product: OrderProduct): string {
    const kind = getOrderPriceSuffixKind(
      this.toRecurrencyItem(product),
      this.orderDetails?.paymentMethod,
      this.orderDetails?.extras
    );
    if (kind === 'perYear') {
      return this.i18nService.instant('components.orderContent.perYear');
    }
    if (kind === 'perMonth') {
      return this.i18nService.instant('components.orderContent.perMonth');
    }
    return '';
  }

  getTotalMonthlySuffix(): string {
    const kind = getOrderTotalSuffixKind(
      this.displayProducts.map((p) => this.toRecurrencyItem(p)),
      this.orderDetails?.paymentMethod,
      this.orderDetails?.extras
    );
    if (kind === 'perYear') {
      return this.i18nService.instant('components.orderContent.perYear');
    }
    if (kind === 'perMonth') {
      return this.i18nService.instant('components.orderContent.perMonth');
    }
    return '';
  }

  hasUtmInUrl(): boolean {
    const url = this.router.url;
    const queryStart = url.indexOf('?');
    if (queryStart === -1) return false;
    const queryString = url.slice(queryStart + 1);
    return Array.from(new URLSearchParams(queryString).keys()).some((k) =>
      k.toLowerCase().startsWith('utm')
    );
  }

  getInstallationAddressLine(): string {
    const addr: InstallationAddress | undefined = this.orderDetails?.installationAddress;
    if (!addr) return '';
    const parts: string[] = [];
    if (addr.street) parts.push(addr.street);
    if (addr.number) parts.push(addr.number);
    if (addr.complement) parts.push(addr.complement);
    if (addr.city || addr.state) {
      parts.push([addr.city, addr.state].filter(Boolean).join('/'));
    }
    if (addr.cep) parts.push(addr.cep);
    return parts.filter(Boolean).join(', ');
  }

  getVisibleRequiredFields(product: OrderProduct): OrderRequiredField[] {
    const fields = product.requiredFields ?? [];
    return fields.filter(
      (f) =>
        DOMAIN_REQUIRED_FIELD_NAMES.has(f.name) && String(f.value ?? '').trim() !== ''
    );
  }

  getRequiredFieldDisplayLabel(field: OrderRequiredField): string {
    const key = `components.orderContent.requiredFieldLabels.${field.name}`;
    const translated = this.i18nService.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    const label = field.label?.trim();
    if (label && label !== field.name) {
      return label;
    }
    return field.name.replace(/_/g, ' ');
  }

  getRequiredFieldDisplayValue(field: OrderRequiredField): string {
    return String(field.value ?? '').trim();
  }

  isDomainValueField(field: OrderRequiredField): boolean {
    return DOMAIN_REQUIRED_FIELD_NAMES.has(field.name);
  }

  getLineItemDomainPreference(product: OrderProduct): DomainPreferenceKind | null {
    const fields = product.requiredFields ?? [];
    for (const name of DOMAIN_PREFERENCE_FIELD_NAMES) {
      const f = fields.find((x) => x.name === name);
      const v = String(f?.value ?? '').trim().toLowerCase();
      if (v === 'new' || v === 'existing' || v === 'transfer') {
        return v;
      }
    }
    return null;
  }

  trackRequiredField(field: OrderRequiredField, index: number): string {
    const id = field.uuid ?? `${field.name}:${field.value}`;
    return `${id}-${index}`;
  }
}
